//WMTO-141-Password Protection for EdgePRO Marketing Portal
import BrasselerColors from "@BrasselerDental/Utils/Colors";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { getSession as getCurrentSession } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import { getHomePageUrl, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SignInChangePasswordForm from "@insite/content-library/Widgets/SignIn/SignInChangePasswordForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    userName = "userName",
    buttonLabel = "buttonLabel",
    modalTitle = "modalTitle",
    passwordLabel = "passwordLabel",
    submitButtonLabel = "submitButtonLabel",
    submittingButtonLabel = "submittingButtonLabel",
    redirectLink = "redirectLink",
    changePasswordInstructions = "changePasswordInstructions",
    fullWidthButton = "fullWidthButton",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.userName]: string;
        [fields.buttonLabel]: string;
        [fields.modalTitle]: string;
        [fields.passwordLabel]: string;
        [fields.submitButtonLabel]: string;
        [fields.submittingButtonLabel]: string;
        [fields.redirectLink]: LinkFieldValue;
        [fields.changePasswordInstructions]: string;
        [fields.fullWidthButton]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    return {
        signedInUserName: session.isAuthenticated && !session.isGuest ? session.userName : undefined,
        homePageUrl: getHomePageUrl(state),
    };
};

const mapDispatchToProps = {
    signIn,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface SignInExistingAccountExtendedStyles {
    openModalButton?: ButtonPresentationProps;
    passwordModal?: ModalPresentationProps;
    form?: InjectableCss;
    signInGridContainer?: GridContainerProps;
    passwordGridItem?: GridItemProps;
    passwordTextField?: TextFieldProps;
    icon?: IconProps;
    signInButtonGridItem?: GridItemProps;
    signInButton?: ButtonPresentationProps;
    signInButtonSpinner?: LoadingSpinnerProps;
    changePasswordModal?: ModalPresentationProps;
}

export const signInExistingAccountExtendedStyles: SignInExistingAccountExtendedStyles = {
    passwordModal: {
        sizeVariant: "small",
        // merge with the theme modal css instead of replacing it
        mergeCss: true,
        cssOverrides: {
            headlineTypography: css`
                color: ${BrasselerColors.blue10};
            `,
        },
    },
    passwordGridItem: {
        width: 12,
    },
    passwordTextField: {
        labelProps: {
            color: BrasselerColors.blue10,
        },
        // theme default for form errors is "warning"; show sign in errors in red
        errorProps: {
            color: "danger",
        },
    },
    icon: {
        css: css`
            padding-top: 50px;
        `,
    },
    signInButtonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    signInButtonSpinner: {
        size: 18,
        color: "primary.contrast",
        css: css`
            margin-right: 8px;
            vertical-align: middle;
        `,
    },
    changePasswordModal: {
        sizeVariant: "small",
    },
};

const styles = signInExistingAccountExtendedStyles;
const StyledForm = getStyledWrapper("form");

const SignInExistingAccountExtended = ({ signIn, fields, signedInUserName, homePageUrl, history }: Props) => {
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [password, setPassword] = useState("");
    const [isSignInClicked, setIsSignInClicked] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCheckingSession, setIsCheckingSession] = useState(false);
    const [closeModalInstantly, setCloseModalInstantly] = useState(false);
    const { url: redirectUrl } = useGetLink(fields.redirectLink);

    const userName = fields.userName?.trim();
    const isSignedInAsConfiguredUser =
        !!userName && !!signedInUserName && signedInUserName.toLowerCase() === userName.toLowerCase();

    // Browser back restores this page from the back/forward cache exactly as it was left - with the modal open and loading.
    // Reset it on pagehide (before the page is cached) so it is already closed when restored. flushSync renders it right away
    // and closeModalInstantly skips the close animation, which would otherwise still be running when the page is frozen.
    // The session in the store was already updated by the sign in before it navigated away.
    useEffect(() => {
        const onPageHide = () => {
            flushSync(() => {
                setCloseModalInstantly(true);
                setIsSubmitting(false);
                setIsPasswordModalOpen(false);
                setPassword("");
            });
        };

        window.addEventListener("pagehide", onPageHide);
        return () => window.removeEventListener("pagehide", onPageHide);
    }, []);

    const signInHandler = (newPassword?: string) => {
        const pass = newPassword || password;
        setIsSignInClicked(true);
        if (!userName || !pass || isSubmitting) {
            return;
        }

        setErrorMessage("");
        // stays on through success - the sign in handler navigates away
        setIsSubmitting(true);

        signIn({
            userName,
            password: pass,
            rememberMe: false,
            keepMeSignedIn: false,
            returnUrl: redirectUrl || undefined,
            onError: (error, statusCode) => {
                setIsSubmitting(false);
                if (statusCode === 422) {
                    setIsPasswordModalOpen(false);
                    setIsChangePasswordModalOpen(true);
                    return;
                }

                setErrorMessage(error);
            },
        });
    };

    const submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        signInHandler();
    };

    // the store session can be stale (idle timeout, page restored from the back/forward cache) - confirm with the server
    const isConfiguredUserStillSignedIn = async () => {
        try {
            const session = await getCurrentSession({});
            return (
                !!session.isAuthenticated &&
                !session.isGuest &&
                session.userName?.toLowerCase() === userName.toLowerCase()
            );
        } catch {
            return false;
        }
    };

    const openPasswordModal = async () => {
        if (isCheckingSession) {
            return;
        }

        // already signed in as the configured user - skip the password modal and go straight to the configured page
        if (isSignedInAsConfiguredUser) {
            setIsCheckingSession(true);
            const stillSignedIn = await isConfiguredUserStillSignedIn();
            setIsCheckingSession(false);

            if (stillSignedIn) {
                const url = redirectUrl || homePageUrl;
                if (url.startsWith("/")) {
                    history.push(url);
                } else {
                    // external links can't be handled by history
                    window.location.href = url;
                }
                return;
            }
            // session expired - ask for the password
        }

        setPassword("");
        setErrorMessage("");
        setIsSignInClicked(false);
        setCloseModalInstantly(false);
        setIsPasswordModalOpen(true);
    };

    const onPasswordChanged = (newPassword: string) => {
        setPassword(newPassword);
        setIsChangePasswordModalOpen(false);
        signInHandler(newPassword);
    };

    const passwordErrorMessage =
        !password && isSignInClicked ? siteMessage("SignInInfo_Password_Required") : errorMessage || "";

    const openModalButtonCss = css`
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    width: 100%;
                `,
                css`
                    width: 100%;
                `,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
            ])}
    `;

    if (!userName) {
        return null;
    }

    const handleClose=() => {
        if(isSubmitting){
            return;
        }
        setIsPasswordModalOpen(false);
    };

    return (
        <>
            <Button
                {...styles.openModalButton}
                css={openModalButtonCss}
                onClick={openPasswordModal}
                disabled={isCheckingSession}
                data-test-selector="signInExtended_openModal"
            >
                {fields.buttonLabel}
            </Button>
            <Modal
                headline={fields.modalTitle}
                {...styles.passwordModal}
                isOpen={isPasswordModalOpen}
                handleClose={handleClose}
                // a 0 length transition closes the modal without the exit animation
                transition={closeModalInstantly ? ({ length: 0 } as ModalPresentationProps["transition"]) : undefined}
                data-test-selector="signInExtended_passwordModal"
            >
                <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
                    <GridContainer {...styles.signInGridContainer}>
                        <GridItem {...styles.passwordGridItem}>
                            <TextField
                                id="signInExtendedPassword"
                                {...styles.passwordTextField}
                                label={fields.passwordLabel}
                                onChange={e => setPassword(e.currentTarget.value)}
                                value={password}
                                error={passwordErrorMessage}
                                iconProps={{
                                    ...styles.icon,
                                    src: showPassword ? EyeOff : Eye,
                                }}
                                iconClickableProps={{
                                    onClick: () => {
                                        setShowPassword(!showPassword);
                                    },
                                    type: "button",
                                }}
                                clickableText={password}
                                autoComplete="current-password"
                                data-test-selector="signInExtended_password"
                                type={showPassword ? "text" : "password"}
                            />
                        </GridItem>
                        <GridItem {...styles.signInButtonGridItem}>
                            <Button
                                {...styles.signInButton}
                                type="submit"
                                disabled={isSubmitting}
                                data-test-selector="signInExtended_submit"
                            >
                                {isSubmitting && <LoadingSpinner {...styles.signInButtonSpinner} />}
                                {isSubmitting
                                    ? fields.submittingButtonLabel || translate("Signing In...")
                                    : fields.submitButtonLabel || translate("Continue")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </StyledForm>
            </Modal>
            <Modal
                headline={translate("Change Password")}
                {...styles.changePasswordModal}
                isOpen={isChangePasswordModalOpen}
                handleClose={() => setIsChangePasswordModalOpen(false)}
            >
                <SignInChangePasswordForm
                    enteredUserName={userName}
                    enteredPassword={password}
                    instructionsText={fields.changePasswordInstructions}
                    onPasswordChanged={onPasswordChanged}
                />
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(SignInExistingAccountExtended)),
    definition: {
       group: "Basic",
        icon: "LogIn",
        displayName: "Sign In With Configured User",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.userName,
                editorTemplate: "TextField",
                displayName: "User Name / Email",
                defaultValue: "",
                isRequired: true,
                sortOrder: 0,
            },
            {
                fieldType: "Translatable",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "Sign In",
                sortOrder: 1,
            },
            {
                fieldType: "Translatable",
                name: fields.modalTitle,
                editorTemplate: "TextField",
                displayName: "Modal Title",
                defaultValue: "Enter Password",
                sortOrder: 2,
            },
            {
                fieldType: "Translatable",
                name: fields.passwordLabel,
                editorTemplate: "TextField",
                displayName: "Password Label",
                defaultValue: "",
                sortOrder: 3,
            },
            {
                fieldType: "Translatable",
                name: fields.submitButtonLabel,
                editorTemplate: "TextField",
                displayName: "Submit Button Label",
                defaultValue: "Continue",
                sortOrder: 4,
            },
            {
                fieldType: "Translatable",
                name: fields.submittingButtonLabel,
                editorTemplate: "TextField",
                displayName: "Submit Button Label (While Signing In)",
                defaultValue: "Signing In...",
                sortOrder: 5,
            },
            {
                fieldType: "Contextual",
                name: fields.redirectLink,
                editorTemplate: "LinkField",
                displayName: "Redirect After Sign In",
                defaultValue: { type: "Page", value: "" },
                sortOrder: 6,
            },
            {
                fieldType: "Translatable",
                name: fields.changePasswordInstructions,
                editorTemplate: "RichTextField",
                displayName: "Change Password Instructions",
                defaultValue: "A password change is required for your account before you may continue.",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                sortOrder: 7,
            },
            {
                name: fields.fullWidthButton,
                displayName: "Full Width Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 8,
            },
        ],
    },
};

export default widgetModule;

//WMTO-141-Password Protection for EdgePRO Marketing Portal
